import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

export interface BulkSendResult {
    succeeded: number;
    failed: number;
    cancelled: boolean;
    /** Fully-built, translated summary shown on the result card, e.g. "88 of 100 sent — 12 failed". */
    message: string;
    variant: "success" | "error";
    /** Optional per-failure reasons, shown when the result card is clicked open. */
    details?: string[];
}

interface RunHelpers {
    onProgress: (done: number, total: number) => void;
    shouldCancel: () => boolean;
}

interface QueuedJob {
    id: string;
    /** Plural noun phrase for the running-state sentence, e.g. "questionnaires". */
    subject: string;
    run: (helpers: RunHelpers) => Promise<BulkSendResult>;
}

export interface ActiveBulkSendJob {
    id: string;
    subject: string;
    done: number;
    total: number;
    cancelRequested: boolean;
}

export interface CompletedBulkSendJob {
    id: string;
    result: BulkSendResult;
}

interface BulkSendContextValue {
    active: ActiveBulkSendJob | null;
    completed: CompletedBulkSendJob | null;
    queueLength: number;
    /** Enqueues a job — runs immediately if the worker is idle, otherwise waits its turn. */
    enqueue: (subject: string, run: QueuedJob["run"]) => void;
    cancelActive: () => void;
    dismissCompleted: () => void;
}

const BulkSendContext = createContext<BulkSendContextValue | null>(null);

// A single shared worker (not one per page) so every bulk-send flow in the app — questionnaires,
// portal links, letters — funnels through the same queue, progress UI, and cancel button instead
// of each page reinventing its own blocking modal.
export function BulkSendProvider({ children }: { children: ReactNode }) {
    const queueRef = useRef<QueuedJob[]>([]);
    const processingRef = useRef(false);
    const cancelRef = useRef(false);
    const [active, setActive] = useState<ActiveBulkSendJob | null>(null);
    const [completed, setCompleted] = useState<CompletedBulkSendJob | null>(null);
    const [queueLength, setQueueLength] = useState(0);

    const processQueue = useCallback(async () => {
        if (processingRef.current) return;
        const next = queueRef.current.shift();
        setQueueLength(queueRef.current.length);
        if (!next) return;

        processingRef.current = true;
        cancelRef.current = false;
        setActive({ id: next.id, subject: next.subject, done: 0, total: 0, cancelRequested: false });

        const result = await next.run({
            onProgress: (done, total) =>
                setActive((prev) => (prev && prev.id === next.id ? { ...prev, done, total } : prev)),
            shouldCancel: () => cancelRef.current,
        });

        setActive(null);
        setCompleted({ id: next.id, result });
        processingRef.current = false;
        void processQueue();
    }, []);

    const enqueue = useCallback((subject: string, run: QueuedJob["run"]) => {
        const id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random());
        queueRef.current.push({ id, subject, run });
        setQueueLength(queueRef.current.length);
        void processQueue();
    }, [processQueue]);

    const cancelActive = useCallback(() => {
        cancelRef.current = true;
        setActive((prev) => (prev ? { ...prev, cancelRequested: true } : prev));
    }, []);

    const dismissCompleted = useCallback(() => setCompleted(null), []);

    return (
        <BulkSendContext.Provider value={{ active, completed, queueLength, enqueue, cancelActive, dismissCompleted }}>
            {children}
        </BulkSendContext.Provider>
    );
}

export function useBulkSend(): BulkSendContextValue {
    const ctx = useContext(BulkSendContext);
    if (!ctx) throw new Error("useBulkSend must be used within a BulkSendProvider");
    return ctx;
}
